import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import './App.css';

// Admin components
import AdminLogin from './components/AdminLogin';
import UnifiedLogin from './components/UnifiedLogin';
import AdminDashboard from './components/Dashboard';
import MainPage from './pages/MainPage';
import AlertDashboard from './components/AlertDashboard';
import DeviceManager from './components/DeviceManager';
import ClientManager from './components/ClientManager';
import ClientAdminManager from './components/ClientAdminManager';
import AdminManager from './components/AdminManager';
import AuditLog from './components/AuditLog';
import { apiFetch } from './api/apiClient';
import SiteDashboard from './components/SiteDashboard';
import SiteManager from './components/SiteManager';
import StarlinkDevicePage from './components/StarlinkDevicePage';
import StarlinkUsageOverview from './components/StarlinkUsageOverview';
import AdminSitesMap from './components/AdminSitesMap';
import AdminBillingPage from './components/billing/AdminBillingPage';
import ClientBillingPage from './components/billing/ClientBillingPage';
import AdminSupportPage from './components/support/AdminSupportPage';
import ClientSupportPage from './components/support/ClientSupportPage';
import AdminSettingsPage from './components/settings/AdminSettingsPage';
import { can } from './utils/permissions';
import { registerForPush, unregisterFromPush } from './utils/pushNotifications';

// Client components
import ClientLogin from './components/ClientLogin';
import ClientDashboard from './components/ClientDashboard';
import ClientSiteDashboard from './components/ClientSiteDashboard';

import ClientVerification from './pages/ClientVerification';
import ResetPassword from './pages/ResetPassword';
import SecurityPage from './pages/SecurityPage';
import TwoFactorPrompt from './components/TwoFactorPrompt';

// Lives in public/, not src/images/ - needed for a stable, non-hashed
// URL the index.html preload tag can target (see index.html for the
// matching <link rel="preload"> tag).
const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';

// True for the Capacitor-wrapped app (iOS/Android/desktop shell), false for
// a plain browser tab. This never changes during the life of the app, so it
// lives at module scope rather than in state - both App() and the
// module-scope PrivateRoute below read the same static value.
const isNative = Capacitor.isNativePlatform();

// Defined at module scope so its component identity is stable across App
// re-renders. Defining it inside App would create a new component type on
// every render, causing React to unmount/remount all routed children (which
// re-runs their effects and duplicates API calls).
const PrivateRoute = ({ children, allowedUserTypes, user, userType }) => {
  if (!user) {
    return <Navigate to={isNative ? "/" : "/login"} />;
  }
  if (allowedUserTypes && !allowedUserTypes.includes(userType)) {
    return <Navigate to={userType === 'admin' ? "/admin/dashboard" : "/dashboard"} />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'admin' or 'client'
  const [isLoading, setIsLoading] = useState(true);
  // Suggest an authenticator app right after a login (not on app relaunch)
  const [show2faPrompt, setShow2faPrompt] = useState(false);

  useEffect(() => {
    // Runs on every login and every app launch (checkAuth sets user). Each
    // run re-claims this device's push token for the current account.
    if (user) {
      registerForPush(user.user_type === 'client' ? 'client' : 'admin');
    }
  }, [user]);

  useEffect(() => {

    const checkAuth = async () => {

      try {

        const response = await apiFetch('/api/auth/me');

        // If still unauthorized after refresh attempt
        if (response.status === 401 || response.status === 422) {
          setUser(null);
          setUserType(null);
          return;
        }

        const user = await response.json();

        setUser(user);
        // Superadmin is an admin for routing purposes; user_type is already
        // "admin" from the server, but normalize defensively in case a role
        // value ever leaks into user_type.
        setUserType(user.user_type === 'client' ? 'client' : 'admin');

      } catch (error) {

        console.error('Auth check failed:', error);
        setUser(null);
        setUserType(null);

      } finally {

        setIsLoading(false);
      }
    };

    checkAuth();

  }, []);

  // Permissions can change while someone is logged in (Settings > Roles).
  // Re-read them whenever the app or tab comes back to the foreground -
  // the iOS/Android apps stay open for days - so menus and pages match
  // what the server will allow. Cheap: one /api/auth/me call.
  useEffect(() => {
    if (!user || user.user_type !== 'client') return undefined;
    const refresh = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const response = await apiFetch('/api/auth/me');
        if (!response.ok) return;
        const fresh = await response.json();
        if (JSON.stringify(fresh.permissions) !== JSON.stringify(user.permissions)) {
          setUser((u) => (u ? { ...u, permissions: fresh.permissions } : u));
        }
      } catch {
        // offline at sea: keep what we have
      }
    };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [user]);

  // Shared by AdminLogin, ClientLogin and UnifiedLogin - all three now
  // return a user object whose user_type field ("admin" | "client") says
  // which auth branch matched, so nothing here needs to know which form
  // was submitted.
  const handleLogin = (userData) => {
    setShow2faPrompt(true);
    setUser(userData);
    setUserType(userData.user_type === 'client' ? 'client' : 'admin');
  };

  const handleLogout = async () => {
    // Unsubscribe this device FIRST, while the auth cookies are still valid.
    // Once /api/auth/logout clears them, the unregister call can no longer
    // authenticate and the device would keep getting this account's alerts.
    await unregisterFromPush();

    try {
      // Determine which logout endpoint to use
      const endpoint = '/api/auth/logout';
      await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setShow2faPrompt(false);
    setUser(null);
    setUserType(null);
  };

  if (isLoading) {
    return (
      <div className="cm-loading-overlay">
        <div className="cm-spinner-container">
          <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
            <source src={loadingVideo} type="video/mp4" />
          </video>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        {user && show2faPrompt && (
          <TwoFactorPrompt onDismiss={() => setShow2faPrompt(false)} />
        )}
        <Routes>
          {/* Admin Routes with /admin prefix */}
          <Route path="/admin/login" element={
            isNative ? <Navigate to="/" /> :
            user ? <Navigate to="/admin/dashboard" /> : <AdminLogin onLogin={handleLogin} />
          } />
          <Route path="/admin/dashboard" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <SiteDashboard user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/alerts" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <AlertDashboard user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/device-manager" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <DeviceManager user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/client-manager" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <ClientManager user={user} />
            </PrivateRoute>
          } />
          <Route path="/admin/admin-manager" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              {(user?.admin_role || user?.role) !== 'superadmin' ? (
                <Navigate to="/admin/client-manager" />
              ) : (
                <AdminManager user={user} onLogout={handleLogout} />
              )}
            </PrivateRoute>
          } />
          {/* Settings: superadmin only. The server checks the role from the DB
              on every request; this redirect just keeps others off the page. */}
          <Route path="/admin/settings" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              {(user?.admin_role || user?.role) !== 'superadmin' ? (
                <Navigate to="/admin/dashboard" />
              ) : (
                <AdminSettingsPage user={user} onLogout={handleLogout} />
              )}
            </PrivateRoute>
          } />
          <Route path="/admin/site-manager" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <SiteManager user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/sites-map" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <AdminSitesMap user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/starlink-usage" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <StarlinkUsageOverview user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/starlink/:terminalId" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <StarlinkDevicePage user={user} onLogout={handleLogout} scope="admin" />
            </PrivateRoute>
          } />
          <Route path="/admin/billing" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <AdminBillingPage user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/support" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <AdminSupportPage user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          <Route path="/admin/audit-log" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin']}>
              <AuditLog user={user} />
            </PrivateRoute>
          } />
          
          {/* Client Routes - clean paths without "client" keyword */}
          <Route path="/login" element={
            isNative ? <Navigate to="/" /> :
            user ? <Navigate to="/dashboard" /> : <ClientLogin onLogin={handleLogin} />
          } />
          <Route path="/dashboard" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['client']}>
              <ClientSiteDashboard user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />

          <Route path="/alerts" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['client']}>
              {userType === 'client' && !can(user, 'alerts.view') ? (
                <Navigate to="/dashboard" />
              ) : (
                <AlertDashboard user={user} userType={userType} onLogout={handleLogout} />
              )}
            </PrivateRoute>
          } />

          <Route path="/starlink/:terminalId" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['client']}>
              <StarlinkDevicePage user={user} onLogout={handleLogout} scope="client" />
            </PrivateRoute>
          } />

          {/* Billing: account admins only by default - keep in step with
              BILLING_CLIENT_ROLES on the server and CLIENT_NAV_LINKS in FleetHeader */}
          <Route path="/billing" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['client']}>
              {!can(user, 'billing.view') ? <Navigate to="/dashboard" /> : <ClientBillingPage user={user} onLogout={handleLogout} />}
            </PrivateRoute>
          } />

          {/* Client pages follow Settings > Roles (utils/permissions.js) */}
          <Route path="/support" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['client']}>
              {!can(user, 'support.use') ? (
                <Navigate to="/dashboard" />
              ) : (
                <ClientSupportPage user={user} onLogout={handleLogout} />
              )}
            </PrivateRoute>
          } />

          <Route path="/user-manager" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['client']}>
              {!can(user, 'users.manage') ? (
                <Navigate to="/dashboard" />
              ) : (
                <ClientAdminManager user={user} onLogout={handleLogout} />
              )}
            </PrivateRoute>
          } />
          
          {/* Monitoring route - accessible to both admin and client (with restrictions) */}
          <Route path="/monitoring/:serialNumber" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin', 'client']}>
                <MainPage userType={userType} user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />
          
          {/* Own security settings (2FA) - same page for admins and clients */}
          <Route path="/security" element={
            <PrivateRoute user={user} userType={userType} allowedUserTypes={['admin', 'client']}>
              <SecurityPage user={user} onLogout={handleLogout} />
            </PrivateRoute>
          } />

          <Route path="/verify" element={<ClientVerification />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          <Route path="/" element={
            user ? (
              <Navigate to={userType === 'client' ? "/dashboard" : "/admin/dashboard"} />
            ) : isNative ? (
              <UnifiedLogin onLogin={handleLogin} />
            ) : (
              <Navigate to="/login" />
            )
          } />

          {/* Catch-all redirect - let "/" above sort out where to send them */}
          <Route path="*" element={
            <Navigate to="/" />
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
